import React from 'react';
import { Grid, Paper, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import type { AvailablePackage, PlanKey } from './pricingCalculatorTypes';

const planPromptPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.38; }
`;

interface PlanPackageSelectorProps {
  availablePackages: AvailablePackage[];
  selectedPackageType: PlanKey | null;
  asDialog: boolean;
  onSelect: (planKey: PlanKey) => void;
}

/**
 * Grid de cards para escolha do plano (Condomínio / Gestão / Institucional). Extraído de
 * UnifiedPricingCalculator.tsx (F7).
 */
export const PlanPackageSelector: React.FC<PlanPackageSelectorProps> = ({
  availablePackages,
  selectedPackageType,
  asDialog,
  onSelect,
}) => {
  return (
    <Grid item xs={12}>
      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{
          fontWeight: 'bold',
          mb: 2,
          ...(asDialog &&
            selectedPackageType == null && {
              animation: `${planPromptPulse} 1.65s ease-in-out infinite`,
              color: 'primary.main',
            }),
        }}
      >
        Escolha o plano que melhor atende seu perfil
      </Typography>
      <Grid container spacing={2}>
        {availablePackages
          .filter((p) => ['condominio', 'gestao', 'institucional', 'operador'].includes((p.key || '').toLowerCase()))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((pkg) => {
            const key = (pkg.key || '').toLowerCase();
            const planKey = (key === 'operador' ? 'gestao' : key) as PlanKey;
            const isSelected = selectedPackageType === planKey;
            return (
              <Grid item xs={12} sm={6} key={pkg.key} sx={{ display: 'flex' }}>
                <Paper
                  onClick={() => onSelect(planKey)}
                  sx={{
                    p: 2,
                    width: '100%',
                    minHeight: { sm: 220 },
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    border: 2,
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected ? 'primary.main' : 'background.paper',
                    color: isSelected ? 'white' : 'inherit',
                    '&:hover': {
                      bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                      borderColor: 'primary.main',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: 'inherit' }}>
                    {pkg.name || pkg.key}
                  </Typography>
                  {pkg.description && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'inherit',
                        opacity: isSelected ? 0.95 : 1,
                        flex: 1,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {pkg.description}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            );
          })}
      </Grid>
    </Grid>
  );
};

export default PlanPackageSelector;
